import { cloneDeep } from 'lodash-es';
import { IDirectDealCurationFilterCondition } from '../../api/directDealCuration/queries/getDirectDealCurations';
import {
  DirectDealSliderFilterKeyType,
  directDealFilterSliderConstant,
} from '../../constants/filter/slider/directDealFilterSliderConstant';
import { IFilter } from '../../interfaces/Filter.interfaces';
import { getDirectDealSliderIndex } from './directDealSliderValue.util';

const SLIDER_CONDITION_KEYS: {
  filterKey: DirectDealSliderFilterKeyType;
  minKey: keyof IDirectDealCurationFilterCondition;
  maxKey: keyof IDirectDealCurationFilterCondition;
}[] = [
  { filterKey: 'engineOutputInPs', minKey: 'minEngineOutputPs', maxKey: 'maxEngineOutputPs' },
  { filterKey: 'price', minKey: 'minPrice', maxKey: 'maxPrice' },
  { filterKey: 'manufacturedYear', minKey: 'minManufacturedYear', maxKey: 'maxManufacturedYear' },
  { filterKey: 'operationTime', minKey: 'minOperationTime', maxKey: 'maxOperationTime' },
];

// 큐레이션 카드의 조건으로 필터를 교체한다. 기존에 걸려 있던 조건과 정렬은 모두 해제한다.
export const applyDirectDealCurationFilterCondition = (
  currentFilters: IFilter[],
  filterCondition: IDirectDealCurationFilterCondition,
): IFilter[] => {
  return cloneDeep(currentFilters).map((filter) => {
    // 카드에 정렬이 지정되어 있으면 그 정렬을, 없으면 기본 정렬을 적용한다.
    if (filter.key === 'sorter') {
      const hasCurationSortOption = filter.options.some((option) => option.value === filterCondition.sort);

      return {
        ...filter,
        options: filter.options.map((option) => ({
          ...option,
          isSelected: hasCurationSortOption ? option.value === filterCondition.sort : Boolean(option.isDefault),
        })),
      };
    }

    const sliderConditionKey = SLIDER_CONDITION_KEYS.find(({ filterKey }) => filterKey === filter.key);

    if (sliderConditionKey) {
      const { filterKey, minKey, maxKey } = sliderConditionKey;
      const sliderConstant = directDealFilterSliderConstant[filterKey];
      const minValue = filterCondition[minKey] as number | undefined;
      const maxValue = filterCondition[maxKey] as number | undefined;

      // 눈금에 없는 값은 슬라이더로 표현할 수 없어 '제한 없음'으로 둔다.
      const minSliderIndex = minValue !== undefined ? getDirectDealSliderIndex(filterKey, minValue) : undefined;
      const maxSliderIndex = maxValue !== undefined ? getDirectDealSliderIndex(filterKey, maxValue) : undefined;

      return {
        ...filter,
        options: filter.options.map((option) => ({
          ...option,
          value:
            option.optionName === 'min' ? minSliderIndex ?? sliderConstant.min : maxSliderIndex ?? sliderConstant.max,
        })),
      };
    }

    const getIsSelected = (optionValue: string, isDefault: boolean): boolean => {
      switch (filter.key) {
        case 'filter_brand':
          // 한 옵션이 여러 브랜드 id를 가질 수 있다(TYM).
          return optionValue.split(',').some((brandId) => filterCondition.brand?.includes(brandId));
        case 'machineType':
          return Boolean(filterCondition.machineType?.includes(optionValue));
        case 'rowConfig':
          return Boolean(filterCondition.rowConfig?.includes(optionValue));
        case 'recentDays':
          return String(filterCondition.recentDays) === optionValue;
        case 'region':
          return Boolean(filterCondition.region?.includes(optionValue));
        default:
          // 카드 조건에 없는 항목(판매 상태)은 기본값으로 되돌린다.
          return isDefault;
      }
    };

    return {
      ...filter,
      options: filter.options.map((option) => ({
        ...option,
        isSelected: getIsSelected(String(option.value), Boolean(option.isDefault)),
      })),
    };
  });
};
